import currency from "currency.js";
import { RangeInput } from "grommet";
import isEqual from "lodash.isequal";
import range from "lodash.range";
import React, { useEffect, useState } from "react";
import { useCounter, useDebounce, useMeasure, useTitle } from "react-use";
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { FormField, Label } from "../../components/forms/shared";
import TextInput from "../../components/forms/TextInput";
import { COLOR_GREEN_600 } from "../../constants/colors";
import { parseNumber, roundToMultiple } from "../../utils/base";
import {
  calcAnnuityBreakdown,
  YearlyBreakdown,
} from "../../utils/calculations";

const AGE_MAX = 92;
const AGE_MIN = 18;

const INTEREST_MAX = 10;

interface CalculatorProps {}

const Calculator: React.FC<CalculatorProps> = () => {
  useTitle("Calculator - Due");
  const [interestRate, { set: setInterestRate }] = useCounter(
    3,
    INTEREST_MAX,
    1
  );
  const [age, setAge] = useState<number>(20);
  const [withdrawalAge, setWithdrawalAge] = useState<number>(65);
  const [yearlyPercentageIncrease, setYearlyPercentageIncrease] =
    useState<string | number>(0);

  const [startingPrincipal, setStartingPrincipal] =
    useState<string | number>(10000);
  const [monthlyContribution, setMonthlyContribution] =
    useState<string | number>(500);
  const [graphData, setGraphData] = useState<YearlyBreakdown | null>(null);
  const [monthlyPayout, setMonthlyPayout] = useState<number | null>(null);

  const [dbInterestRate, { set: setDbInterestRate }] = useCounter(
    3.5,
    INTEREST_MAX,
    1
  );

  const [dbYearlyPercentageIncrease, { set: setDbYearlyPercentageIncrease }] =
    useCounter(0, 100, 1);

  const [dbAge, { set: setDbAge }] = useCounter(20, AGE_MAX, AGE_MIN);
  const [dbWithdrawalAge, { set: setDbWithdrawalAge }] = useCounter(
    65,
    AGE_MAX + 1,
    AGE_MIN
  );
  const [dbStartingPrincipal, { set: setDbStartingPrincipal }] =
    useCounter(1000);
  const [dbMonthlyContribution, { set: setDbMonthlyContribution }] =
    useCounter(100);
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  useDebounce(
    () => {
      setDbInterestRate(interestRate);
      setDbAge(age);
      setDbWithdrawalAge(withdrawalAge);
      setDbYearlyPercentageIncrease(yearlyPercentageIncrease as number);
      setDbStartingPrincipal(currency(startingPrincipal).value);
      setDbMonthlyContribution(currency(monthlyContribution).value);
    },
    750,
    [
      interestRate,
      age,
      withdrawalAge,
      startingPrincipal,
      monthlyContribution,
      yearlyPercentageIncrease,
    ]
  );

  useEffect(() => {
    const { yearlyBreakdown, monthlyPayout, yearlyContributions } =
      calcAnnuityBreakdown(
        dbInterestRate,
        dbAge,
        dbWithdrawalAge,
        dbYearlyPercentageIncrease,
        dbStartingPrincipal,
        dbMonthlyContribution
      );
    setGraphData(yearlyBreakdown);
    setMonthlyPayout(monthlyPayout);
    console.log(yearlyContributions);
  }, [
    dbInterestRate,
    dbAge,
    dbWithdrawalAge,
    dbYearlyPercentageIncrease,
    dbStartingPrincipal,
    dbMonthlyContribution,
  ]);

  useEffect(() => {
    if (age >= withdrawalAge) {
      setWithdrawalAge(age + 1);
    }
  }, [age, withdrawalAge, setWithdrawalAge]);

  return (
    <div className="p-8 h-full w-full flex min-h-0">
      <div className="w-80 h-full flex-none">
        <div className="text-xl mb-4">Contributions</div>
        <FormField id="monthlyContribution" label="Monthly Contribution">
          <TextInput
            type="number"
            id="monthlyContribution"
            name="monthlyContribution"
            value={monthlyContribution}
            onChange={(e) => {
              setMonthlyContribution(e.target.value);
            }}
            onBlur={(e) => {
              const value: any = parseFloat(
                e.target.value.replace(/,/g, "")
              ).toFixed(2);

              if (isNaN(value)) {
                setMonthlyContribution(0);
              } else {
                setMonthlyContribution(value);
              }
            }}
          />
        </FormField>

        <FormField id="startingPrincipal" label="One Time Contribution">
          <TextInput
            type="number"
            id="startingPrincipal"
            name="startingPrincipal"
            value={startingPrincipal}
            onChange={(e) => {
              setStartingPrincipal(e.target.value);
            }}
            onBlur={(e) => {
              const value: any = parseFloat(
                e.target.value.replace(/,/g, "")
              ).toFixed(2);

              if (isNaN(value)) {
                setStartingPrincipal(0);
              } else {
                setStartingPrincipal(value);
              }
            }}
          />
        </FormField>

        <FormField
          id="yearlyPercentageIncrease"
          label="Yearly Contribution Increase Percentage"
        >
          <TextInput
            type="number"
            id="yearlyPercentageIncrease"
            name="yearlyPercentageIncrease"
            value={yearlyPercentageIncrease}
            onChange={(e) => {
              setYearlyPercentageIncrease(e.target.value);
            }}
            onBlur={(e) => {
              const value: any = parseFloat(e.target.value.replace(/,/g, ""));

              if (isNaN(value)) {
                setYearlyPercentageIncrease(0);
              } else {
                setYearlyPercentageIncrease(value);
              }
            }}
          />
        </FormField>
        <div className="text-xl mb-4">Rates & Ages</div>
        <Label>Interest Rate</Label>
        <div className="flex items-center">
          <RangeInput
            min={0}
            max={INTEREST_MAX}
            value={interestRate}
            onChange={(e) => {
              setInterestRate(parseNumber(e.target.value));
            }}
            style={{ minWidth: 180 }}
            step={0.1}
          />
          <TextInput
            className="ml-2"
            type="number"
            min={0}
            max={INTEREST_MAX}
            value={interestRate}
            onChange={(e) => {
              setInterestRate(parseNumber(e.target.value));
            }}
          />
        </div>

        <Label>Current Age</Label>
        <div className="flex items-center">
          <RangeInput
            type="number"
            min={AGE_MIN}
            max={AGE_MAX}
            value={age}
            onChange={(e) => {
              const number = parseNumber(e.target.value);
              setAge(number);
            }}
            style={{ minWidth: 180 }}
          />
          <TextInput
            className="ml-2"
            type="number"
            min={AGE_MAX}
            max={AGE_MAX}
            value={age}
            onChange={(e) => {
              setAge(e.target.value as any);
            }}
            onBlur={(e) => {
              const value: any = parseInt(e.target.value.replace(/,/g, ""));

              if (isNaN(value)) {
                setAge(20);
              } else if (value > AGE_MAX) {
                setAge(AGE_MAX);
              } else if (value < AGE_MIN) {
                setAge(AGE_MIN);
              } else {
                setAge(value);
              }
            }}
          />
        </div>

        <Label>Retirement Age</Label>
        <div className="flex items-center">
          <RangeInput
            min={age + 1}
            max={AGE_MAX - 1}
            value={withdrawalAge}
            onChange={(e) => {
              const number = parseNumber(e.target.value);
              setWithdrawalAge(number);
            }}
            style={{ minWidth: 180 }}
          />
          <TextInput
            className="ml-2"
            type="number"
            min={age + 1}
            max={AGE_MAX - 1}
            value={withdrawalAge}
            onChange={(e) => {
              setWithdrawalAge(e.target.value as any);
            }}
            onBlur={(e) => {
              const value: any = parseInt(e.target.value.replace(/,/g, ""));

              if (isNaN(value)) {
                setWithdrawalAge(age + 1);
              } else if (value > AGE_MAX) {
                setWithdrawalAge(AGE_MAX);
              } else if (value < age) {
                setWithdrawalAge(age + 1);
              } else {
                setWithdrawalAge(value);
              }
            }}
          />
        </div>
      </div>
      <div className="flex flex-col h-full w-full pl-6 min-h-0">
        <div className="flex">
          {graphData && (
            <div>
              <div className="text-3xl">
                {currency(
                  graphData.find((data) => data.age === withdrawalAge)
                    ?.balance || 100
                ).format()}
              </div>
              <div className="text-sm text-gray-500">
                Balance at {withdrawalAge}
              </div>
            </div>
          )}
          {monthlyPayout && (
            <div className="ml-auto text-right">
              <div className="text-3xl">{currency(monthlyPayout).format()}</div>
              <div className="text-sm text-gray-500">
                Monthly Payouts at {withdrawalAge}
              </div>
            </div>
          )}
        </div>
        <div ref={ref} className="flex-auto pt-6 min-h-0">
          {graphData && <CalculatorGraph height={height} data={graphData} />}
        </div>
      </div>
    </div>
  );
};

export const CalculatorGraph = React.memo(
  ({ data, height }: { data: YearlyBreakdown; height: number }) => {
    const startingBalance = data[0].balance;
    const finalBalance = data[data.length - 1].balance;

    let multiplier;

    if (finalBalance >= 5000000) {
      multiplier = 5;
    } else if (finalBalance >= 500000) {
      multiplier = 4;
    } else if (finalBalance >= 100000) {
      multiplier = 4;
    } else if (finalBalance >= 50000) {
      multiplier = 3;
    } else {
      multiplier = 2;
    }

    const ticksY = [
      ...range(
        roundToMultiple(
          startingBalance - 5 * Math.pow(10, multiplier),
          5 * Math.pow(10, multiplier)
        ),
        finalBalance,
        5 * Math.pow(10, multiplier)
      ),
      finalBalance,
    ];

    return (
      <ResponsiveContainer height={height}>
        <LineChart
          data={data.map(({ balance, age, gains, principal, interest }) => {
            return {
              age,
              balance: currency(balance).value,
              gains: currency(gains).value,
              principal: currency(principal).value,
              interest: currency(interest).value,
            };
          })}
          margin={{ top: 10, right: 10, left: 35, bottom: 0 }}
        >
          <XAxis
            dataKey="age"
            tick={{ fontSize: 12 }}
            interval={"preserveStartEnd"}
          />
          <YAxis
            tickFormatter={(value) => {
              return currency(value, { precision: 0 }).format();
            }}
            ticks={ticksY}
            tick={{ fontSize: 12 }}
            domain={["dataMin - 500", "dataMax + 1000"]}
          />
          <Line
            type="linear"
            dataKey="balance"
            stroke={COLOR_GREEN_600}
            strokeWidth={2}
            dot={{ stroke: COLOR_GREEN_600, strokeWidth: 2 }}
          />
          <Tooltip />
        </LineChart>
      </ResponsiveContainer>
    );
  },
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  }
);

export default Calculator;
